import React, { useContext, useEffect, useRef, useState, useMemo } from "react"
import InputFieldWithLabel from "~/components/Admin/Inputs/InputFieldWithLabel"
import InputFieldWithCurrency from "~/components/Admin/Inputs/InputFieldWithCurrency"
import ProductDropdown from "~/components/Admin/Products/ProductDropdown"
import { TrashIcon } from "@heroicons/react/24/solid"
import ILineItem from "~/interfaces/ILineItem"
import { LINE_ITEM_KINDS } from "~/constants/line_item_kinds"
import { RESERVATION_KINDS } from "~/constants"
import { SiteProps } from "~/interfaces"
import { NewReservationAction } from "~/reducers/newReservation"
import { NewReservationContext } from "~/contexts/admin/NewReservationContext"
import { debounce } from "lodash"
import { emptyLineItemAddonId } from "~/components/Admin/Reservations/NewReservationModal/NewReservationPayment"

interface ILineItemRowProps {
    row: Partial<ILineItem>
    sites: SiteProps[]
    selectProduct: (product: any, row: Partial<ILineItem>) => void
    createNewItem: (name: string, id?: string) => void
    deleteAddon: (row: ILineItem, isAddOn: boolean, isNew: boolean) => void
    dispatch: any
    camp: any
    isLoadingOrderPreview: boolean
}

const ResponsiveLineItemRow: React.FC<ILineItemRowProps> = ({
    row,
    sites,
    selectProduct,
    createNewItem,
    deleteAddon,
    dispatch,
    camp,
    isLoadingOrderPreview,
}) => {
    const { lineItems, userLineItems, newReservation, setUserLineItems } = useContext(NewReservationContext)

    const [localUnitPrice, setLocalUnitPrice] = useState(row.unit_amount_in_cents ? row.unit_amount_in_cents / 100 : 0)
    const [localQuantity, setLocalQuantity] = useState(row.quantity || 1)
    const [localTotal, setLocalTotal] = useState(row.total_amount_in_cents ? row.total_amount_in_cents / 100 : 0)
    const lastFocusedRef = useRef<HTMLInputElement | null>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setLocalUnitPrice(row.unit_amount_in_cents ? row.unit_amount_in_cents / 100 : 0)
    }, [row.unit_amount_in_cents])

    useEffect(() => {
        setLocalQuantity(row.quantity || 1)
    }, [row.quantity])

    useEffect(() => {
        setLocalTotal(row.total_amount_in_cents ? row.total_amount_in_cents / 100 : 0)
    }, [row.total_amount_in_cents])

    useEffect(() => {
        if (!isLoadingOrderPreview && lastFocusedRef.current) {
            lastFocusedRef.current.focus()
        }
    }, [isLoadingOrderPreview])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur()
                }
                lastFocusedRef.current = null
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [])

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        lastFocusedRef.current = e.target
    }

    const handleUnitPriceBlur = () => {
        const mLineItems = [...lineItems]
        const idx = mLineItems.findIndex((li) => li.add_on_id === row.add_on_id)
        if (idx !== -1) {
            const newUnitCents = localUnitPrice * 100
            if (mLineItems[idx].unit_amount_in_cents !== newUnitCents) {
                mLineItems[idx].unit_amount_in_cents = newUnitCents
                // Recalculate total (assuming total = unit price * quantity * occurrences)
                mLineItems[idx].total_amount_in_cents = newUnitCents * localQuantity * (mLineItems[idx].occurrences || 1)
                setUserLineItems(mLineItems)
            }
        }
    }

    const handleQuantityBlur = () => {
        const mLineItems = [...lineItems]
        const idx = mLineItems.findIndex((li) => li.add_on_id === row.add_on_id)
        if (idx !== -1 && mLineItems[idx].quantity !== localQuantity) {
            mLineItems[idx].quantity = localQuantity
            // Recalculate total
            mLineItems[idx].total_amount_in_cents =
                mLineItems[idx].unit_amount_in_cents * localQuantity * (mLineItems[idx].occurrences || 1)
            setUserLineItems(mLineItems)
        }
    }

    const handleTotalBlur = () => {
        const mLineItems = [...lineItems]
        const idx = mLineItems.findIndex((li) => li.add_on_id === row.add_on_id)
        if (idx !== -1) {
            const occurrences = mLineItems[idx].occurrences || 1
            const quantity = mLineItems[idx].quantity || 1
            const newTotalCents = Math.round(localTotal * 100)
            mLineItems[idx].total_amount_in_cents = newTotalCents
            mLineItems[idx].unit_amount_in_cents = newTotalCents / (quantity * occurrences)
            if (mLineItems[idx].reservation_id) {
                dispatch({
                    type: NewReservationAction.EDITED_TOTAL,
                })
                dispatch({
                    type: NewReservationAction.UPDATE_SUBTOTAL,
                    payload: { subtotal: Number(localTotal) },
                })
            }
            setUserLineItems(mLineItems)
        }
    }

    const changeLineItemLabel = debounce((value: string, id?: string) => {
        const mLineItems = [...lineItems]
        const addonIdx = mLineItems.findIndex((li) => li.add_on_id !== emptyLineItemAddonId && li.add_on_id === id)
        if (addonIdx !== -1) {
            mLineItems[addonIdx].label = value
            setUserLineItems(mLineItems)
        }
    }, 300)

    const isAddOn = !!row.add_on_id
    const isCustomNew = !row.add_on_id && !row.reservation_id && row.label !== "Reservation"
    const isAlwaysApplied = !!row.is_always_applied
    const isFee = !!row.parent_line_item_id

    const labelComponent = useMemo(() => {
        if (!isAddOn || isCustomNew || isAlwaysApplied) {
            return (
                <InputFieldWithLabel
                    type="text"
                    value={row?.label}
                    onChange={(e) => changeLineItemLabel(e.target.value, row.add_on_id)}
                    className="cursor-default focus:border-none focus:ring-0"
                    onFocus={handleFocus}
                    autocomplete="off"
                    disabled={
                        newReservation.kind === RESERVATION_KINDS.GROUP || isAlwaysApplied || !!row?.reservation_id || isFee
                    }
                />
            )
        } else {
            return (
                <ProductDropdown
                    site={sites?.find((s) => s.id === newReservation.site_id)}
                    disabledProductIds={[...(lineItems || []), ...(userLineItems || [])].map((li) => li.add_on_id)}
                    onChange={(product) => selectProduct(product, row)}
                    createNewItem={createNewItem}
                    selectedId={row.add_on_id}
                    lineItem={row}
                />
            )
        }
    }, [
        isAddOn,
        isCustomNew,
        isAlwaysApplied,
        row?.label,
        row.add_on_id,
        newReservation.kind,
        isFee,
        row?.reservation_id,
        sites,
        newReservation.site_id,
        lineItems,
        userLineItems,
        selectProduct,
        createNewItem,
        handleFocus,
        changeLineItemLabel
    ]);

    const resetTotalButton = row.reservation_id && newReservation.edited_total && (
        <div>
            <a
                className="underline text-sm text-green-550 hover:text-green-500 hover:underline cursor-pointer"
                onClick={() => {
                    dispatch({ type: NewReservationAction.UNEDITED_TOTAL })
                    dispatch({
                        type: NewReservationAction.UPDATE_SUBTOTAL,
                        payload: { subtotal: null },
                    })
                }}
            >
                Reset total
            </a>
        </div>
    )

    const deleteButton = useMemo(() => {
        if (!row.add_on_id || row.is_always_applied) {
            return null
        }

        return (
            <button
                disabled={isLoadingOrderPreview}
                onClick={() => deleteAddon(row as ILineItem, !!row.add_on_id, !row.reservation_id)}
                className="flex hover:bg-gray-100 rounded-md p-2 justify-center items-center"
            >
                <TrashIcon className="w-6 h-6 cursor-pointer" />
            </button>
        )
    }, [
        row.add_on_id,
        row.is_always_applied,
        row.reservation_id,
        isLoadingOrderPreview,
        deleteAddon
    ]);

    return (
        <>
            <div className="md:hidden rounded-lg shadow-sm border border-gray-30 p-3 mb-4">
                <div className="flex justify-between items-start mb-3">
                    <div className="flex flex-wrap items-center w-full">
                        <div className="flex-grow mr-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Item</label>
                            {labelComponent}
                        </div>

                        {row.occurrences && (
                            <div className="w-20">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nights</label>
                                <InputFieldWithLabel type="number" disabled value={row.occurrences} />
                            </div>
                        )}
                    </div>
                    {deleteButton && <div className="ml-2 mt-6">
                        {deleteButton}
                    </div>}
                </div>

                <div className="flex items-start gap-2">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                        <InputFieldWithCurrency
                            currency={camp.currency}
                            hideCurrency
                            disabled={
                                isLoadingOrderPreview ||
                                (!isAddOn && row.kind !== LINE_ITEM_KINDS.BASIC) ||
                                isAlwaysApplied ||
                                row.reservation_id
                            }
                            value={localUnitPrice || null}
                            onChange={(e) => setLocalUnitPrice(e.target.value)}
                            onBlur={handleUnitPriceBlur}
                            onFocus={handleFocus}
                        />
                    </div>

                    <div className="w-20">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Qty</label>
                        <InputFieldWithLabel
                            type="number"
                            disabled={isLoadingOrderPreview || !row.add_on_id || isAlwaysApplied || isFee}
                            disableArrows
                            value={localQuantity}
                            min={1}
                            onChange={(e) => setLocalQuantity(e.target.value)}
                            onBlur={handleQuantityBlur}
                            onFocus={handleFocus}
                        />
                    </div>

                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Total Price</label>
                        <div>
                            <InputFieldWithCurrency
                                currency={camp.currency}
                                hideCurrency
                                disabled={
                                    isLoadingOrderPreview ||
                                    (!isAddOn && row.kind !== LINE_ITEM_KINDS.BASIC) ||
                                    isAlwaysApplied
                                }
                                value={localTotal}
                                onChange={(e) => setLocalTotal(e.target.value)}
                                onBlur={handleTotalBlur}
                                onFocus={handleFocus}
                            />
                            {resetTotalButton}
                        </div>
                    </div>
                </div>
            </div>

            <React.Fragment>
                <div className="hidden md:block mb-2.5 relative" ref={wrapperRef}>
                    {labelComponent}
                </div>
                <div className="hidden md:block mb-2.5 w-full">
                    <InputFieldWithCurrency
                        currency={camp.currency}
                        hideCurrency
                        disabled={
                            isLoadingOrderPreview ||
                            (!isAddOn && row.kind !== LINE_ITEM_KINDS.BASIC) ||
                            isAlwaysApplied ||
                            row.reservation_id
                        }
                        value={localUnitPrice || null}
                        onChange={(e) => setLocalUnitPrice(Number(e.target.value))}
                        onBlur={handleUnitPriceBlur}
                        onFocus={handleFocus}
                    />
                </div>
                <div className="hidden md:block mb-2.5">
                    {row.occurrences ? <InputFieldWithLabel type="number" disabled value={row.occurrences} /> : <div />}
                </div>
                <div className="hidden md:block mb-2.5">
                    <InputFieldWithLabel
                        type="number"
                        disabled={isLoadingOrderPreview || !row.add_on_id || isAlwaysApplied || isFee}
                        disableArrows
                        value={localQuantity}
                        min={1}
                        onChange={(e) => setLocalQuantity(e.target.value)}
                        onBlur={handleQuantityBlur}
                        onFocus={handleFocus}
                    />
                </div>
                <div className="hidden md:flex flex-col items-end justify-center mb-2.5">
                    <InputFieldWithCurrency
                        currency={camp.currency}
                        hideCurrency
                        disabled={isLoadingOrderPreview || (!isAddOn && row.kind !== LINE_ITEM_KINDS.BASIC) || isAlwaysApplied}
                        value={localTotal}
                        onChange={(e) => setLocalTotal(e.target.value)}
                        onBlur={handleTotalBlur}
                        onFocus={handleFocus}
                    />
                    {resetTotalButton}
                </div>
                <div className="hidden md:flex items-center mb-2.5">
                    {!row.add_on_id && <div className="w-full" />}
                    {row.add_on_id && !row.is_always_applied && (
                        <button
                            disabled={isLoadingOrderPreview}
                            onClick={() => deleteAddon(row as ILineItem, !!row.add_on_id, !row.reservation_id)}
                            className="w-full flex hover:bg-gray-100 rounded-md p-2 justify-center items-center"
                        >
                            <TrashIcon className="w-6 h-6 cursor-pointer" />
                        </button>
                    )}
                </div>
            </React.Fragment>
        </>
    )
}

export default ResponsiveLineItemRow